/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/AnnotationSettingsView/AnnotationSettingsView" />
import * as React from "react";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue, ObservableValue, useObservable } from "VSS/Core/Observable";
import { usePageContext } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { Header, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { BoardAnnotationsIdentifiers, MaxAnnotationLimit } from "Wit/BoardsHub/Common/AnnotationUtils";
import { IAnnotationSetting } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { Rule } from "Wit/Clients/Work/Contracts/Work";
import { IAnnotationSettingsData } from "Wit/Common/KanbanBoardSettings/Components/AnnotationSettingsView/AnnotationSettingsInterfaces";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";
import { TableStatus } from "Wit/Common/WitSettingsViews/Components/TableStatus";
import { getIconDataObservable } from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";

export function getAnnotationSettingsView(
    annotations: IAnnotationSettingsData[],
    originalAnnotations: IAnnotationSetting[],
    disabled?: boolean,
    groupId?: string,
    onSaveSettings?: () => Promise<void>
) {
    const isDirty = new ObservableValue(false);
    const isValid = new ObservableValue(true);
    return {
        groupId,
        isDirty,
        isValid,
        name: Resources.AnnotationsTitle,
        onSaveSettings,
        renderContent: () => (
            <AnnotationSettingsView
                disabled={disabled}
                isDirty={isDirty}
                isValid={isValid}
                originalAnnotations={originalAnnotations}
                annotations={annotations}
            />
        )
    };
}

export function getAnnotationsAsRules(annotations: IAnnotationSettingsData[]): Rule[] {
    return annotations.map(annotation => {
        return {
            name: annotation.id,
            isEnabled: annotation.isEnabled.value.toString(),
            filter: "", // don't need to use filter string, all data is in clauses
            clauses: [],
            settings: {}
        };
    });
}

export function getAnnotationData(annotations: IAnnotationSetting[]): IAnnotationSettingsData[] {
    return annotations.map(annotation => ({ id: annotation.id, isEnabled: new ObservableValue(annotation.isEnabled), name: annotation.name }));
}

interface IAnnotationSettingsViewProps {
    annotations: IAnnotationSettingsData[];
    disabled?: boolean;
    isDirty: IObservableValue<boolean>;
    isValid: IObservableValue<boolean>;
    originalAnnotations: IAnnotationSetting[];
}

const AnnotationSettingsView: React.FunctionComponent<IAnnotationSettingsViewProps> = ({
    annotations,
    disabled,
    isDirty,
    isValid,
    originalAnnotations
}) => {
    const pageContext = usePageContext();
    const projectId = getTFSProject(pageContext)?.id ?? "";
    const itemProvider = React.useRef(new ArrayItemProvider(annotations));
    const [errorMessage] = useObservable<string | undefined>(undefined);

    const updateDirtyState = () => {
        const updatedIsDirty = areAnnotationsDirty(annotations, originalAnnotations);
        if (updatedIsDirty !== isDirty.value) {
            isDirty.value = updatedIsDirty;
        }

        const updatedIsValid = annotations.filter(annotation => annotation.isEnabled.value).length <= MaxAnnotationLimit;
        const updatedErrorMessage = !updatedIsValid ? Resources.TooManyAnnotationsErrorMessage : undefined;
        if (updatedErrorMessage !== errorMessage.value) {
            errorMessage.value = updatedErrorMessage;
        }

        if (updatedIsValid !== isValid.value) {
            isValid.value = updatedIsValid;
        }
    };

    const renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IAnnotationSettingsData>,
        tableItem: IAnnotationSettingsData
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                {tableItem.name}
            </SimpleTableCell>
        );
    };

    const renderVisualizationColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IAnnotationSettingsData>,
        tableItem: IAnnotationSettingsData
    ): JSX.Element => {
        let icon: JSX.Element | undefined;
        if (tableItem.id === BoardAnnotationsIdentifiers.TestAnnotation) {
            icon = <Icon iconName="TestBeakerSolid" />;
        } else if (tableItem.id === BoardAnnotationsIdentifiers.GitHubLinksAnnotation) {
            icon = <Icon iconName="GitHubLogo" />;
        } else {
            const iconData = getIconDataObservable(projectId, tableItem.name, pageContext);
            icon = <WorkItemTypeIcon data={iconData} />;
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                {icon}
            </SimpleTableCell>
        );
    };

    const renderEnabledColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IAnnotationSettingsData>,
        tableItem: IAnnotationSettingsData
    ): JSX.Element => {
        const onChange = (event: any, value: boolean) => {
            tableItem.isEnabled.value = value;
            updateDirtyState();
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <Checkbox ariaLabel={Resources.EnabledLabel} checked={tableItem.isEnabled} onChange={onChange} disabled={disabled} />
            </SimpleTableCell>
        );
    };

    const columns: ITableColumn<IAnnotationSettingsData>[] = [
        {
            id: "annotationName",
            name: Resources.AnnotationColumnName,
            renderCell: renderNameColumn,
            width: new ObservableValue(-33.33)
        },
        {
            id: "visualization",
            name: Resources.VisualizationColumnName,
            renderCell: renderVisualizationColumn,
            width: new ObservableValue(-33.33)
        },
        {
            id: "isEnabled",
            name: Resources.EnabledColumnName,
            renderCell: renderEnabledColumn,
            width: new ObservableValue(-33.33)
        }
    ];

    return (
        <div className="rhythm-vertical-8">
            <Header
                className="padding-vertical-0"
                description={Resources.AnnotationsDescription}
                title={Resources.AnnotationsTitle}
                titleSize={TitleSize.Small}
            />
            <TableStatus className="padding-horizontal-20" validationErrorMessage={errorMessage} />
            <Table ariaLabel={Resources.AnnotationsTitle} columns={columns} itemProvider={itemProvider.current} showLines={false} />
        </div>
    );
};

function areAnnotationsDirty(annotations: IAnnotationSettingsData[], originalAnnotations: IAnnotationSetting[]): boolean {
    const originalAnnotationsMap = new Map<string, boolean>();
    for (const originalAnnotation of originalAnnotations) {
        originalAnnotationsMap.set(originalAnnotation.id, originalAnnotation.isEnabled);
    }

    return !!annotations.find(annotation => annotation.isEnabled.value !== originalAnnotationsMap.get(annotation.id));
}
